<script lang="ts" setup>
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { computed, ref, watch } from "vue";
import { useError } from "@/composables/useError";
import { useI18n } from "vue-i18n";
import { API } from "@/api/api";
import OrderField from "@/components/inputs/OrderField.vue";

const api = new API();
const { showAPIError } = useError();
const { t } = useI18n();

const MENU_WIDTH = 1248;
const MENU_OFFSET: [number, number] = [-200, 200];

const props = defineProps<{
  truck: TruckDto;
}>();

const emit = defineEmits<{
  (e: "update", truck: TruckDto): void;
}>();

const draft = ref<Record<string, string>>({});
const saved = ref<Record<string, string>>({});
const isSaving = ref(false);
const isOpen = ref(false);

function setDefaultNotes() {
  const next: Record<string, string> = {
    truck: props.truck.note ?? "",
  };

  if (props.truck.driver) {
    next["driver.driverNote"] = props.truck.driver.driverNoteText ?? "";
    next["driver.note"] = props.truck.driver.note ?? "";
  }

  if (props.truck.driver2) {
    next["driver2.driverNote"] = props.truck.driver2.driverNoteText ?? "";
    next["driver2.note"] = props.truck.driver2.note ?? "";
  }

  draft.value = { ...next };
  saved.value = { ...next };
}

const hasNotesChanged = computed(() => {
  return Object.keys(saved.value).some((key) => draft.value[key] !== saved.value[key]);
});

const hasNotes = computed(() => Object.values(saved.value).some(Boolean));

function buildDriverPatch(notePrefix: string): Record<string, string> {
  const data: Record<string, string> = {};

  if (draft.value[`${notePrefix}.note`] !== saved.value[`${notePrefix}.note`]) {
    data.note = draft.value[`${notePrefix}.note`];
  }

  if (draft.value[`${notePrefix}.driverNote`] !== saved.value[`${notePrefix}.driverNote`]) {
    data.driverNote = draft.value[`${notePrefix}.driverNote`];
  }

  return data;
}

function buildUpdatedTruck(): TruckDto {
  return {
    ...props.truck,
    note: draft.value.truck,
    driver: props.truck.driver
      ? { ...props.truck.driver, note: draft.value["driver.note"], driverNoteText: draft.value["driver.driverNote"] }
      : props.truck.driver,
    driver2: props.truck.driver2
      ? { ...props.truck.driver2, note: draft.value["driver2.note"], driverNoteText: draft.value["driver2.driverNote"] }
      : props.truck.driver2,
  };
}

async function save() {
  if (!hasNotesChanged.value || isSaving.value) {
    return;
  }

  isSaving.value = true;

  try {
    const requests = [];

    if (draft.value.truck !== saved.value.truck && props.truck.id != null) {
      requests.push(api.trucks.updateTruck(props.truck.id, { note: draft.value.truck }));
    }

    const driverPatch = buildDriverPatch("driver");

    if (Object.keys(driverPatch).length && props.truck.driverId != null) {
      requests.push(api.users.updateDriver(props.truck.driverId, driverPatch));
    }

    const driver2Patch = buildDriverPatch("driver2");

    if (Object.keys(driver2Patch).length && props.truck.driver2Id != null) {
      requests.push(api.users.updateDriver(props.truck.driver2Id, driver2Patch));
    }

    const responses = await Promise.all(requests);
    const failed = responses.find((response) => response.errorData);

    if (failed) {
      showAPIError(failed, t("trucks.update-note-failed"));
      return;
    }

    saved.value = { ...draft.value };
    emit("update", buildUpdatedTruck());
  } finally {
    isSaving.value = false;
  }
}

watch(() => props.truck, setDefaultNotes, { immediate: true });
</script>

<template>
  <v-menu v-model="isOpen" :close-on-content-click="false" :offset="MENU_OFFSET" :width="MENU_WIDTH">
    <template #activator="{ props: menuProps }">
      <v-btn
        :class="hasNotes ? 'text-primary' : 'text-grey-darken-1'"
        class="quote-btn"
        data-qa="truck-notes-button"
        size="small"
        v-bind="menuProps"
        variant="flat"
      >
        <v-icon size="small">mdi-format-quote-close</v-icon>
      </v-btn>
    </template>

    <v-card class="elevation-12 border position-relative">
      <v-btn
        class="position-absolute"
        density="comfortable"
        icon="mdi-close"
        style="top: 8px; right: 8px"
        variant="text"
        @click="isOpen = false"
      />

      <div class="py-5 px-6">
        <order-field v-model="draft.truck">
          <template #title>
            <span class="font-size-14 text-grey-darken-3">{{ t("trucks.truck-note") }}</span>
          </template>
        </order-field>

        <v-row v-if="truck.driver" class="mt-3">
          <v-col cols="6">
            <order-field v-model="draft['driver.driverNote']">
              <template #title>
                <span class="font-size-14 text-grey-darken-3">{{ t("trucks.driver-note") }}</span>
              </template>
            </order-field>
          </v-col>
          <v-col cols="6">
            <order-field v-model="draft['driver.note']">
              <template #title>
                <span class="font-size-14 text-grey-darken-3">{{ t("trucks.dispatcher-note-first") }}</span>
              </template>
            </order-field>
          </v-col>
        </v-row>

        <v-row v-if="truck.driver2" class="mt-3">
          <v-col cols="6">
            <order-field v-model="draft['driver2.driverNote']">
              <template #title>
                <span class="font-size-14 text-grey-darken-3">{{ t("trucks.second-driver-note") }}</span>
              </template>
            </order-field>
          </v-col>
          <v-col cols="6">
            <order-field v-model="draft['driver2.note']">
              <template #title>
                <span class="font-size-14 text-grey-darken-3">{{ t("trucks.dispatcher-note-second") }}</span>
              </template>
            </order-field>
          </v-col>
        </v-row>
      </div>

      <v-card-actions class="d-flex justify-end pa-4">
        <v-btn :disabled="!hasNotesChanged" :loading="isSaving" color="primary" variant="flat" @click="save">{{
          t("general.save")
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
.quote-btn {
  position: absolute;
  top: 2px;
  right: 4px;
  min-width: auto !important;
  height: auto !important;
  background-color: transparent !important;
}
</style>
